import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { CornerDownRight } from "lucide-react";
import { TruncatedTextTooltip } from "components/common/TruncatedTextTooltip";
import { formatRelativeTime } from "../utils/formatRelativeTime";
import { ActionControl } from "./ActionControl";
import { HistorySentence } from "./HistorySentence";
import { LAST_EVENT_KEYS } from "./constants";
import {
  actorNameOf,
  avatarClassOf,
  blockedReasonOf,
  isCurrentUserActor,
} from "./utils";
import type { HistoryEntryRowProps } from "./types";

export const HistoryEntryRow = ({
  entry,
  currentUserEmail,
  revertingEntryId,
  onRevert,
}: HistoryEntryRowProps) => {
  const { t, i18n } = useTranslation();

  const adminLabel = t("smartSurveyor.history.admin");
  const actorName = actorNameOf(entry.actor, adminLabel);
  const actorLabel = isCurrentUserActor(entry.actor, currentUserEmail)
    ? t("smartSurveyor.history.you")
    : actorName;

  const relativeTime = (isoDate: string): string =>
    formatRelativeTime({ isoDate, now: new Date(), locale: i18n.language });

  // Depth 0 is the change itself; odd rows undid it, even rows redid it.
  const isChange = entry.depth === 0;
  const isUndone = isChange && entry.lastEvent?.direction === "undo";
  // Pressing the button on an even row undoes the change, on an odd row redoes it.
  const buttonDirection = entry.depth % 2 === 0 ? "undo" : "redo";

  const blockedReason =
    entry.action === null
      ? null
      : blockedReasonOf({
          action: entry.action,
          direction: buttonDirection,
          currentUserEmail,
          formatTime: relativeTime,
          adminLabel,
        });

  return (
    <li className="flex gap-3 border-b border-gray-100 py-3">
      <span
        className={clsx(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
          avatarClassOf(entry.actor),
        )}
      >
        {actorName.charAt(0).toUpperCase()}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <TruncatedTextTooltip
            text={actorLabel}
            className="text-sm font-medium text-gray-800"
          />
          <time
            className="shrink-0 text-xs text-gray-500"
            dateTime={entry.createdAt}
            title={new Date(entry.createdAt).toLocaleString(i18n.language)}
          >
            · {relativeTime(entry.createdAt)}
          </time>
          <span className="ml-auto flex shrink-0 items-center">
            <ActionControl
              entryId={entry.id}
              planCount={entry.planCount}
              action={entry.action}
              direction={buttonDirection}
              blockedReason={blockedReason}
              isReverting={revertingEntryId !== null}
              onRevert={onRevert}
            />
          </span>
        </div>
        {isChange ? (
          <p
            className={clsx(
              "text-sm",
              isUndone ? "text-gray-500 line-through" : "text-gray-700",
            )}
          >
            <HistorySentence
              summary={entry.summary}
              planCount={entry.planCount}
            />
          </p>
        ) : (
          <>
            <p className="text-sm font-medium text-gray-800">
              {t(
                entry.depth % 2 === 1
                  ? "smartSurveyor.history.revertedChange"
                  : "smartSurveyor.history.reappliedChange",
              )}
            </p>
            <p className="border-l-2 border-gray-200 pl-2 text-xs text-gray-500">
              <HistorySentence
                summary={entry.summary}
                planCount={entry.planCount}
              />
            </p>
          </>
        )}
        {/* A row without a button was replaced by a later step; this line says which. */}
        {entry.lastEvent !== null && (
          <p className="flex items-center gap-1 text-xs text-gray-900">
            <CornerDownRight size={12} />
            {t(
              `smartSurveyor.history.${
                LAST_EVENT_KEYS[entry.lastEvent.direction][
                  isCurrentUserActor(entry.lastEvent.actor, currentUserEmail)
                    ? "byYou"
                    : "byOther"
                ]
              }`,
              {
                actor: actorNameOf(entry.lastEvent.actor, adminLabel),
                time: relativeTime(entry.lastEvent.createdAt),
              },
            )}
          </p>
        )}
      </div>
    </li>
  );
};
